import React, { useCallback, useMemo } from 'react';
import { string, shape, arrayOf, bool, func, number } from 'prop-types';

import Forms from 'components/form-react-elements';
import Icon from 'components/icon';
import { format, isset } from 'utils';
import { CollapsibleFilters } from './components';
import { ActiveFilters } from 'components/FilteredListing/components';

const Filters = ({
    activeFilters,
    clearFiltersButtonTitle,
    clearFilterTitle,
    closeButtonTitle,
    filters,
    filtersModalTitle,
    filtersTitle,
    filtersTitleIsHiddenOnMobile,
    handleClearFilters,
    isLoading,
    labelCollapse,
    labelExpand,
    numberFormat,
    onCheckboxChange,
    onCloseButtonClick,
    results,
    showResultsButtonTitle
}) => {
    const formattedResults =
        isset(results) && numberFormat
            ? format.number(results, numberFormat)
            : '';

    const handleCheckboxChange = (event, filterId, controlledFilterId) => {
        onCheckboxChange(event.target.id, filterId, controlledFilterId);
    };

    const getFormattedOptionLabel = option =>
        `${option.label} (${format.number(option.total, numberFormat)})`;

    const getFormattedOptions = useCallback(
        options => {
            if (!options || !Array.isArray(options) || !options.length) {
                return [];
            }

            return options.map(option => {
                const formattedOption = {
                    ...option,
                    label:
                        typeof option.total === 'number'
                            ? getFormattedOptionLabel(option)
                            : option.label,
                    /** Disable checkbox if total is zero. */
                    disabled:
                        typeof option.total === 'number' && option.total < 1
                            ? true
                            : option.disabled
                };

                if (option.subOptions && option.subOptions.length) {
                    formattedOption.subOptions = option.subOptions.map(
                        subOption => ({
                            ...subOption,
                            label:
                                typeof subOption.total === 'number'
                                    ? getFormattedOptionLabel(subOption)
                                    : subOption.label,
                            /** Disable checkbox if total is zero. */
                            disabled:
                                typeof subOption.total === 'number' &&
                                subOption.total < 1
                                    ? true
                                    : subOption.disabled
                        })
                    );
                }
                return formattedOption;
            });
        },
        [filters]
    );

    const isExpanded = useCallback(
        options =>
            options.some(option => {
                if (option.subOptions && option.subOptions.length) {
                    return (
                        option.checked ||
                        option.subOptions.some(subOption => subOption.checked)
                    );
                }

                return option.checked;
            }),
        []
    );

    const renderFilter = item => {
        const { id, label, options } = item;

        return (
            <Forms.CheckboxGroup
                disabled={isLoading}
                key={id}
                label={label}
                name={id}
                options={getFormattedOptions(options)}
                onChange={event => handleCheckboxChange(event, id)}
                checkedLabelIsBold
                isVertical
                labelIsHidden
            />
        );
    };

    if (!filters || !filters.length) {
        return null;
    }

    return (
        <div className="listing-filters">
            <div className="wrapper">
                <div className="listing-filters__header-container">
                    <div className="listing-filters__header">
                        {filtersTitleIsHiddenOnMobile ? (
                            <h2 className="h3">{filtersModalTitle}</h2>
                        ) : (
                            <span className="h3">{filtersModalTitle}</span>
                        )}

                        {closeButtonTitle && (
                            <div className="listing-filters__close-button-container">
                                <button
                                    className="listing-filters__close-button"
                                    type="button"
                                    onClick={onCloseButtonClick}
                                >
                                    {closeButtonTitle}
                                    <Icon glyph="cross" />
                                </button>
                            </div>
                        )}
                    </div>
                    <ActiveFilters
                        activeFilters={activeFilters}
                        activeIndex={0}
                        clearFiltersButtonTitle={clearFiltersButtonTitle}
                        clearFilterTitle={clearFilterTitle}
                        handleClearFilters={handleClearFilters}
                        onCheckboxChange={onCheckboxChange}
                    />
                </div>
                {filtersTitle && (
                    <h2
                        className={`listing-filters__title h4 ${
                            filtersTitleIsHiddenOnMobile
                                ? 'listing-filters__title--only-desktop'
                                : ''
                        }`}
                    >
                        {filtersTitle}
                    </h2>
                )}
                <div className="listing-filters__block">
                    {filters.map(item => {
                        if (item.isCollapsible) {
                            return (
                                <CollapsibleFilters
                                    id={`filter-${item.id}`}
                                    labelCollapse={labelCollapse}
                                    labelExpand={labelExpand}
                                    isExpanded={isExpanded(item.options)}
                                    key={item.id}
                                    title={item.label}
                                >
                                    {renderFilter(item)}
                                </CollapsibleFilters>
                            );
                        }

                        return (
                            <div
                                key={item.id}
                                className="no-collapsible-filters"
                            >
                                {renderFilter(item)}
                            </div>
                        );
                    })}
                </div>
                {showResultsButtonTitle && (
                    <div className="listing-filters__show-results-button-container">
                        <button
                            className="button button--primary listing-filters__show-results-button"
                            disabled={isLoading}
                            onClick={onCloseButtonClick}
                        >
                            {showResultsButtonTitle} ({formattedResults})
                            <Icon glyph="arrow-right" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

Filters.propTypes = {
    activeFilters: arrayOf(shape({})).isRequired,
    clearFiltersButtonTitle: string,
    clearFilterTitle: string,
    closeButtonTitle: string,
    filters: arrayOf(shape({})).isRequired,
    filtersModalTitle: string,
    filtersTitle: string,
    filtersTitleIsHiddenOnMobile: bool,
    handleClearFilters: func,
    isLoading: bool.isRequired,
    labelCollapse: string.isRequired,
    labelExpand: string.isRequired,
    numberFormat: shape({
        precision: number,
        thousand: string,
        decimal: string
    }),
    onCheckboxChange: func.isRequired,
    onCloseButtonClick: func,
    results: number,
    showResultsButtonTitle: string
};

export default Filters;
